import { type MutableRefObject, useEffect, useRef, useState } from 'react';

/**
 * React hook for checking whether a referenced element is in the viewport or not.
 * The viewport is based on the window.
 * @internal
 */
export function useIsInViewport<T>(): [MutableRefObject<T | null>, boolean] {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);

  const callback = (entries: IntersectionObserverEntry[]) => {
    // Filter entries for our target element
    const targetEntries = entries.filter(
      (entry) => entry.target === containerRef.current,
    );

    if (targetEntries.length === 0) {
      return;
    }

    // Find the most recent entry by comparing time property
    const mostRecentEntry = targetEntries.reduce((latest, current) =>
      current.time > latest.time ? current : latest,
    );

    setIsVisible(mostRecentEntry.isIntersecting);
  };

  useEffect(() => {
    const refElement = containerRef.current;
    const observer = new IntersectionObserver(callback);
    if (refElement) {
      observer.observe(refElement);
    }

    return () => {
      // clean up
      if (refElement) {
        observer.unobserve(refElement);
      }
    };
  }, [containerRef]);

  return [containerRef, isVisible];
}
